// =============================================
// LAYOUT RESPONSIVE CON SUBMENUS
// Con menú hamburguesa y submenús colapsables
// ACTUALIZADO: Incluye Conciliación Bancaria
// =============================================

import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Payment as PaymentIcon,
  Money as MoneyIcon,
  CalendarToday as CalendarIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  WhatsApp as WhatsAppIcon,
  Backup as BackupIcon,
  Assessment as ReportIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Home as HomeIcon,
  Description as DescriptionIcon,
  Warning as WarningIcon,
  CloudUpload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  VerifiedUser as VerifyIcon,
  Business as BusinessIcon,
  AccountBalance as AccountBalanceIcon,
  AccountBalanceWallet as BankIcon,
  ExpandLess,
  ExpandMore,
  AttachMoney as FinanceIcon,
  Receipt as ReceiptIcon,
  SyncAlt as SyncIcon,
  CreditCard as CreditCardIcon,
  Engineering as EngineeringIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import ChangePasswordDialog from '../Auth/ChangePasswordDialog';

const DRAWER_WIDTH = 260;

const ResponsiveLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Estados para submenús
  const [openSubmenus, setOpenSubmenus] = useState({
    finanzas: false,
    configuracion: false,
    notificaciones: false,
    conciliacion: false, // NUEVO
  });

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const toggleSubmenu = (submenu) => {
    setOpenSubmenus({
      ...openSubmenus,
      [submenu]: !openSubmenus[submenu],
    });
  };

  // Menús con estructura de submenús
  const adminMenuStructure = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Usuarios', icon: <PeopleIcon />, path: '/users' },
    { text: 'Comunidades', icon: <BusinessIcon />, path: '/communities' },
    { text: 'Proveedores', icon: <EngineeringIcon />, path: '/service-providers' },

    // Submenú Finanzas
    {
      text: 'Finanzas',
      icon: <FinanceIcon />,
      submenu: 'finanzas',
      children: [
        { text: 'Pagos', icon: <PaymentIcon />, path: '/payments' },
        { text: 'Verificar Pagos', icon: <VerifyIcon />, path: '/admin/receipts' },
        { text: 'Gastos', icon: <MoneyIcon />, path: '/expenses' },
        { text: 'Deudores', icon: <WarningIcon />, path: '/debts' },
        { text: 'Estados de Cuenta', icon: <ReportIcon />, path: '/account-statement' },
      ],
    },

    // ========== NUEVO: Submenú Conciliación Bancaria ==========
    {
      text: 'Conciliación Bancaria',
      icon: <SyncIcon />,
      submenu: 'conciliacion',
      children: [
        { text: 'Importar Estado', icon: <ReceiptIcon />, path: '/admin/bank-reconciliation' },
        { text: 'Transacciones', icon: <SyncIcon />, path: '/admin/bank-reconciliation?tab=transactions' },
        { text: 'Cuentas Bancarias', icon: <CreditCardIcon />, path: '/admin/bank-accounts' },
      ],
    },
    // ========================================================

    // Submenú Configuración
    {
      text: 'Configuración',
      icon: <SettingsIcon />,
      submenu: 'configuracion',
      children: [
        { text: 'Períodos', icon: <CalendarIcon />, path: '/periods' },
        { text: 'Parámetros', icon: <SettingsIcon />, path: '/parameters' },
        { text: 'Info Bancaria', icon: <AccountBalanceIcon />, path: '/bank-accounts' },
        { text: 'Bancos', icon: <BankIcon />, path: '/banks' },
      ],
    },

    { text: 'Carga Masiva', icon: <UploadIcon />, path: '/bulk-upload' },

    // Submenú Notificaciones
    {
      text: 'Notificaciones',
      icon: <NotificationsIcon />,
      submenu: 'notificaciones',
      children: [
        { text: 'Email', icon: <NotificationsIcon />, path: '/notifications' },
        { text: 'WhatsApp', icon: <WhatsAppIcon />, path: '/whatsapp' },
      ],
    },

    { text: 'Backups', icon: <BackupIcon />, path: '/backup' },
  ];

  const ownerMenuItems = [
    { text: 'Mi Dashboard', icon: <HomeIcon />, path: '/owner/dashboard' },
    { text: 'Mis Pagos', icon: <PaymentIcon />, path: '/owner/payments' },
    { text: 'Subir Pago', icon: <CloudUploadIcon />, path: '/owner/upload-payment' },
    { text: 'Mi Estado', icon: <DescriptionIcon />, path: '/owner/statement' },
    { text: 'Gastos', icon: <MoneyIcon />, path: '/owner/expenses' },
    { text: 'Info Bancaria', icon: <AccountBalanceIcon />, path: '/owner/bank-info' },
  ];

  const menuStructure = user?.role === 'admin' ? adminMenuStructure : ownerMenuItems;

  // Función para renderizar items del menú
  const renderMenuItem = (item, index) => {
    const isSubmenuParent = item.submenu;
    const isSubmenuOpen = openSubmenus[item.submenu];
    const isSelected = location.pathname === item.path;

    if (isSubmenuParent) {
      return (
        <React.Fragment key={index}>
          {/* Item padre con submenú */}
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => toggleSubmenu(item.submenu)}
              sx={{
                borderRadius: 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  fontWeight: 500,
                }}
              />
              {isSubmenuOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>

          {/* Submenú colapsable */}
          <Collapse in={isSubmenuOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child, childIndex) => {
                const isChildSelected = location.pathname === child.path;
                return (
                  <ListItem key={childIndex} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      selected={isChildSelected}
                      onClick={() => handleNavigate(child.path)}
                      sx={{
                        pl: 4,
                        borderRadius: 1,
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          '& .MuiListItemIcon-root': {
                            color: 'white',
                          },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: isChildSelected ? 'white' : 'inherit',
                        }}
                      >
                        {child.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={child.text}
                        primaryTypographyProps={{
                          fontSize: '0.9rem',
                          fontWeight: isChildSelected ? 600 : 400,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    // Item normal sin submenú
    return (
      <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
        <ListItemButton
          selected={isSelected}
          onClick={() => handleNavigate(item.path)}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '& .MuiListItemIcon-root': {
                color: 'white',
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
              color: isSelected ? 'white' : 'inherit',
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontSize: '0.95rem',
              fontWeight: isSelected ? 600 : 400,
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawer = (
    <Box>
      {/* Logo / Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'primary.main',
          color: 'white',
          minHeight: 64,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1.5,
          }}
        >
          <HomeIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h6" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {user?.community_name || 'Villas System'}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            Sistema FI
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* User Info */}
      <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 1.5 }}>
            {user?.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {user?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.role === 'admin' ? 'Administrador' : 'Propietario'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* Menu Items */}
      <List sx={{ px: 1 }}>
        {menuStructure.map((item, index) => renderMenuItem(item, index))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: 1,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography variant="h6" noWrap component="div">
              {location.pathname.split('/').pop().replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Dashboard'}
            </Typography>
          </Box>

          <Box>
            <IconButton onClick={handleMenuClick} size="large">
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem disabled>
                <ListItemIcon>
                  <AccountIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={user?.name}
                  secondary={user?.email}
                />
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => {
                handleMenuClose();
                setPasswordDialogOpen(true);
              }}>
                <ListItemIcon>
                  <LockIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Cambiar Contraseña</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Cerrar Sesión</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
        }}
      >
        {drawer}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, zIndex: (theme) => theme.zIndex.drawer },
        }}
        open
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          bgcolor: 'grey.50',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      {/* Dialogo de cambio de contraseña */}
      <ChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
      />
    </Box>
  );
};

export default ResponsiveLayout;
